import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { TrendingUp, Plus, Loader2, X, MapPin, Pencil, Trash2, Building2, Euro, Percent, ImagePlus, Paperclip, FileText, Pin, MessagesSquare, Calculator, Bell, BellPlus } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

const PROPERTY_TYPES = ["Appartement", "Maison", "Immeuble", "Studio", "Local commercial", "Parking", "Autre"];
const fmtEur = (n) => (n ? `${Number(n).toLocaleString("fr-FR")} €` : "—");
const EMPTY = { title: "", city: "", property_type: "Appartement", project_detail: "", reasons: "", sector_strengths: "", rent_hypotheses: "", purchase_price: "", works_estimate: "", rentability: "", photos: [], documents: [], sim: {} };
const SIM_FIELDS = [
  { k: "prix", label: "Prix d'acquisition (€)" },
  { k: "frais", label: "Frais d'acquisition (€)" },
  { k: "travaux", label: "Travaux (€)" },
  { k: "apport", label: "Apport (€)" },
  { k: "duree", label: "Durée du prêt (ans)" },
  { k: "taux", label: "Taux d'intérêt (%)" },
  { k: "assurance", label: "Taux assurance emprunteur (%)" },
  { k: "loyer", label: "Loyer / mois (€)" },
  { k: "taxe", label: "Taxe foncière / an (€)" },
  { k: "assurances_pno", label: "Assurance PNO / an (€)" },
  { k: "charges", label: "Autres charges / an (€)" },
];
const fileUrl = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

export default function OffresDeals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canPublish = ["agent", "admin"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const canSubscribe = ["bailleur", "gestionnaire", "locataire", "admin"].includes(user?.role);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null | {...}
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");
  const photoInput = useRef(null);
  const docInput = useRef(null);
  const openedDeal = useRef(false);

  // Alertes bons deals
  const [alerts, setAlerts] = useState([]);
  const [alertTypes, setAlertTypes] = useState([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertForm, setAlertForm] = useState({ city: "", property_type: "", price_max: "", rentability_min: "" });
  const [alertBusy, setAlertBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/deals");
      const list = r.data || [];
      setDeals(list);
      const params = new URLSearchParams(location.search);
      const dealId = params.get("deal");
      if (dealId && !openedDeal.current) {
        const d = list.find((x) => x.id === dealId);
        if (d) { setDetail(d); openedDeal.current = true; }
      }
    }
    catch { toast.error("Impossible de charger les annonces"); }
    finally { setLoading(false); }
  };
  const loadAlerts = async () => {
    try { const r = await api.get("/deal-alerts"); setAlerts(r.data.alerts || []); setAlertTypes(r.data.property_types || []); }
    catch { /* silencieux */ }
  };
  useEffect(() => { load(); if (canSubscribe) loadAlerts(); }, []);

  const createAlert = async () => {
    setAlertBusy(true);
    try {
      const payload = {
        city: alertForm.city.trim(),
        property_type: alertForm.property_type,
        price_max: Number(alertForm.price_max) || 0,
        rentability_min: Number(alertForm.rentability_min) || 0,
      };
      await api.post("/deal-alerts", payload);
      toast.success("Alerte créée — vous serez prévenu(e) par cloche et e-mail");
      setAlertForm({ city: "", property_type: "", price_max: "", rentability_min: "" });
      loadAlerts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setAlertBusy(false); }
  };
  const removeAlert = async (id) => {
    try { await api.delete(`/deal-alerts/${id}`); toast.success("Alerte supprimée"); loadAlerts(); }
    catch { toast.error("Erreur"); }
  };
  const alertLabel = (a) => {
    const parts = [];
    parts.push(a.city ? a.city : "Toutes villes");
    if (a.property_type) parts.push(a.property_type);
    if (a.price_max > 0) parts.push(`≤ ${Number(a.price_max).toLocaleString("fr-FR")} €`);
    if (a.rentability_min > 0) parts.push(`≥ ${a.rentability_min}%`);
    return parts.join(" · ");
  };

  const openCreate = () => setForm({ ...EMPTY });
  const openEdit = (d) => setForm({ ...EMPTY, ...d, photos: d.photos || [], documents: d.documents || [] });

  const uploadPhotos = async (files) => {
    if (!files?.length) return;
    setUploading("photo");
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const r = await api.post("/deals/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((cur) => ({ ...cur, photos: [...(cur.photos || []), r.data.url] }));
      }
      toast.success("Photo(s) ajoutée(s)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'upload"); }
    finally { setUploading(""); if (photoInput.current) photoInput.current.value = ""; }
  };
  const uploadDocs = async (files) => {
    if (!files?.length) return;
    setUploading("doc");
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const r = await api.post("/deals/upload-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((cur) => ({ ...cur, documents: [...(cur.documents || []), { url: r.data.url, filename: r.data.filename }] }));
      }
      toast.success("Document(s) ajouté(s)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'upload"); }
    finally { setUploading(""); if (docInput.current) docInput.current.value = ""; }
  };
  const removePhoto = (i) => setForm((cur) => ({ ...cur, photos: cur.photos.filter((_, idx) => idx !== i) }));
  const removeDoc = (i) => setForm((cur) => ({ ...cur, documents: cur.documents.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Le titre est requis"); return; }
    setBusy(true);
    const payload = {
      ...form,
      purchase_price: Number(form.purchase_price) || 0,
      works_estimate: Number(form.works_estimate) || 0,
      rentability: Number(form.rentability) || 0,
      photos: form.photos || [],
      documents: form.documents || [],
      sim: Object.fromEntries(SIM_FIELDS.map(({ k }) => [k, Number(form.sim?.[k]) || 0])),
    };
    delete payload._id; delete payload.created_at; delete payload.agent_id; delete payload.agent_name; delete payload.status;
    try {
      if (form.id) await api.put(`/deals/${form.id}`, payload);
      else await api.post("/deals", payload);
      toast.success(form.id ? "Annonce mise à jour" : "Annonce publiée");
      setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const remove = async (d) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try { await api.delete(`/deals/${d.id}`); toast.success("Annonce supprimée"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const togglePin = async (d) => {
    try {
      await api.put(`/deals/${d.id}/pin`, { pinned: !d.pinned });
      toast.success(!d.pinned ? "Annonce épinglée en tête" : "Épinglage retiré");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const contactAgent = async (d) => {
    try {
      const subject = `Bonjour, je suis intéressé(e) par votre annonce « ${d.title} »${d.city ? ` à ${d.city}` : ""}. Pouvez-vous m'en dire plus ?`;
      await api.post("/conversations/start", { name: d.agent_name || "Agent immobilier", property: d.title, subject });
      toast.success("Message envoyé à l'agent");
      setDetail(null);
      navigate("/app/messagerie");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const hasSimData = (d) => d?.sim && SIM_FIELDS.some(({ k }) => Number(d.sim[k]) > 0);
  const analyze = (d) => {
    const s = d.sim || {};
    // Valeurs par défaut : reprend les données du deal, avec repli sur prix d'achat / travaux
    const sim = {
      prix: Number(s.prix) || Number(d.purchase_price) || 0,
      frais: Number(s.frais) || 0,
      travaux: Number(s.travaux) || Number(d.works_estimate) || 0,
      apport: Number(s.apport) || 0,
      duree: Number(s.duree) || 20,
      taux: Number(s.taux) || 3.5,
      assurance: Number(s.assurance) || 0.34,
      loyer: Number(s.loyer) || 0,
      taxe: Number(s.taxe) || 0,
      assurances_pno: Number(s.assurances_pno) || 0,
      charges: Number(s.charges) || 0,
    };
    navigate("/app/simulateur", { state: { sim, name: `${d.title}${d.city ? ` — ${d.city}` : ""}` } });
  };

  const [savingSim, setSavingSim] = useState(false);
  const saveSimScenario = async (d) => {
    const s = d.sim || {};
    const inputs = {
      prix: Number(s.prix) || Number(d.purchase_price) || 0, frais: Number(s.frais) || 0,
      travaux: Number(s.travaux) || Number(d.works_estimate) || 0, apport: Number(s.apport) || 0,
      duree: Number(s.duree) || 20, taux: Number(s.taux) || 3.5, assurance: Number(s.assurance) || 0.34,
      loyer: Number(s.loyer) || 0, taxe: Number(s.taxe) || 0, assurances_pno: Number(s.assurances_pno) || 0, charges: Number(s.charges) || 0,
    };
    setSavingSim(true);
    try {
      await api.post("/sim/scenarios", { name: `${d.title}${d.city ? ` — ${d.city}` : ""}`, inputs, results: {} });
      toast.success("Simulation enregistrée dans « Mes scénarios »");
    } catch (e) { toast.error("Enregistrement impossible"); }
    finally { setSavingSim(false); }
  };

  return (
    <div className="space-y-6" data-testid="offres-deals-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><TrendingUp className="w-5 h-5" /></div>
          <div>
            <h2 className="font-display text-2xl font-semibold text-brand-greenDeep">Offres sélectionnées & bons deals</h2>
            <p className="text-sm text-brand-green/55">Des opportunités d'investissement analysées par nos agents</p>
          </div>
        </div>
        {canPublish && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold transition-colors" data-testid="publish-deal-btn">
            <Plus className="w-4 h-4" /> Publier une annonce
          </button>
        )}
        {canSubscribe && (
          <button onClick={() => setAlertOpen((v) => !v)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-sm font-semibold transition-colors" data-testid="deal-alerts-btn">
            <Bell className="w-4 h-4" /> Mes alertes{alerts.length > 0 ? ` (${alerts.length})` : ""}
          </button>
        )}
      </div>

      {canSubscribe && alertOpen && (
        <div className="bg-white rounded-3xl border border-brand-soft p-6" data-testid="deal-alerts-panel">
          <div className="flex items-center gap-2 mb-1">
            <BellPlus className="w-5 h-5 text-brand-greenDeep" />
            <h3 className="font-display text-lg font-semibold text-brand-greenDeep">Alertes bons deals</h3>
          </div>
          <p className="text-sm text-brand-green/55 mb-4">Créez une alerte : dès qu'une nouvelle annonce correspond à vos critères, vous êtes prévenu(e) par la cloche 🔔 et par e-mail.</p>
          <div className="grid md:grid-cols-4 gap-3">
            <Field label="Ville"><input value={alertForm.city} onChange={(e) => setAlertForm({ ...alertForm, city: e.target.value })} className={inp} placeholder="Toutes" data-testid="alert-city" /></Field>
            <Field label="Type de bien">
              <select value={alertForm.property_type} onChange={(e) => setAlertForm({ ...alertForm, property_type: e.target.value })} className={inp} data-testid="alert-type">
                <option value="">Tous</option>
                {(alertTypes.length ? alertTypes : PROPERTY_TYPES).map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Prix max (€)"><input type="number" value={alertForm.price_max} onChange={(e) => setAlertForm({ ...alertForm, price_max: e.target.value })} className={inp} placeholder="Aucun" data-testid="alert-price-max" /></Field>
            <Field label="Rentabilité min (%)"><input type="number" step="0.1" value={alertForm.rentability_min} onChange={(e) => setAlertForm({ ...alertForm, rentability_min: e.target.value })} className={inp} placeholder="Aucune" data-testid="alert-rentability-min" /></Field>
          </div>
          <button onClick={createAlert} disabled={alertBusy} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60 transition-colors" data-testid="create-alert-btn">
            {alertBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Créer l'alerte
          </button>
          {alerts.length > 0 && (
            <div className="mt-5 space-y-2" data-testid="alerts-list">
              <p className="text-xs font-semibold text-brand-green/50 uppercase tracking-wide">Mes alertes actives</p>
              {alerts.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-brand-softer" data-testid={`alert-row-${a.id}`}>
                  <span className="text-sm text-brand-greenDeep font-medium flex items-center gap-2"><Bell className="w-4 h-4 text-brand-green/50" />{alertLabel(a)}</span>
                  <button onClick={() => removeAlert(a.id)} className="p-1.5 rounded-full hover:bg-red-50 text-red-500" title="Supprimer" data-testid={`delete-alert-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24 text-brand-green/50"><Loader2 className="w-7 h-7 animate-spin" /></div>
      ) : deals.length === 0 ? (
        <div className="text-center py-24 text-brand-green/50" data-testid="no-deals">
          Aucune annonce publiée pour le moment.{canPublish && " Cliquez sur « Publier une annonce » pour commencer."}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {deals.map((d) => (
            <div key={d.id} className="bg-white rounded-3xl border border-brand-soft overflow-hidden flex flex-col" data-testid="deal-card">
              {d.photos?.length > 0 && (
                <div className="relative h-44 bg-brand-softer">
                  <img src={fileUrl(d.photos[0])} alt={d.title} className="w-full h-full object-cover" />
                  {d.photos.length > 1 && <span className="absolute bottom-2 right-2 text-xs font-semibold px-2 py-1 rounded-full bg-black/55 text-white">+{d.photos.length - 1} photo{d.photos.length > 2 ? "s" : ""}</span>}
                </div>
              )}
              <div className="p-6 flex flex-col flex-1">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold px-3 py-1 rounded-full bg-brand-lime/30 text-brand-greenDeep">{d.property_type}</span>
                  {d.pinned && <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-brand-greenDeep text-brand-lime" data-testid="pinned-badge"><Pin className="w-3 h-3" />À la une</span>}
                </div>
                {d.rentability ? <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600"><Percent className="w-3.5 h-3.5" />{d.rentability}% rentabilité</span> : null}
              </div>
              <h3 className="font-display text-lg font-semibold text-brand-greenDeep mt-3 leading-snug">{d.title}</h3>
              {d.city && <p className="text-sm text-brand-green/55 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" />{d.city}</p>}
              <p className="text-sm text-brand-green/70 mt-2 leading-relaxed line-clamp-3 flex-1">{d.project_detail}</p>
              <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
                <div className="bg-brand-softer rounded-xl px-3 py-2"><p className="text-xs text-brand-green/45">Prix d'achat</p><p className="font-semibold text-brand-greenDeep">{fmtEur(d.purchase_price)}</p></div>
                <div className="bg-brand-softer rounded-xl px-3 py-2"><p className="text-xs text-brand-green/45">Travaux</p><p className="font-semibold text-brand-greenDeep">{fmtEur(d.works_estimate)}</p></div>
              </div>
              {d.documents?.length > 0 && <p className="text-xs text-brand-green/45 mt-3 inline-flex items-center gap-1"><Paperclip className="w-3.5 h-3.5" />{d.documents.length} document{d.documents.length > 1 ? "s" : ""}</p>}
              <div className="flex items-center gap-2 mt-4">
                <button onClick={() => setDetail(d)} className="flex-1 py-2.5 rounded-full bg-brand-soft hover:bg-brand-mint text-brand-greenDeep text-sm font-semibold transition-colors" data-testid="view-deal-btn">Voir le détail</button>
                {hasSimData(d) && (
                  <button onClick={() => analyze(d)} className="p-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep" title="Analyser la rentabilité dans le simulateur" data-testid="analyze-deal-btn"><Calculator className="w-4 h-4" /></button>
                )}
                {isAdmin && (
                  <button onClick={() => togglePin(d)} className={`p-2.5 rounded-full transition-colors ${d.pinned ? "bg-brand-greenDeep text-brand-lime" : "bg-brand-softer hover:bg-brand-soft text-brand-greenDeep"}`} title={d.pinned ? "Retirer de la une" : "Épingler en tête"} data-testid="pin-deal-btn"><Pin className="w-4 h-4" /></button>
                )}
                {canPublish && d.agent_id === user?.user_id && (
                  <>
                    <button onClick={() => openEdit(d)} className="p-2.5 rounded-full bg-brand-softer hover:bg-brand-soft text-brand-greenDeep" title="Modifier"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(d)} className="p-2.5 rounded-full bg-red-50 hover:bg-red-100 text-red-500" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
              </div>
              <p className="text-xs text-brand-green/40 mt-3">Publié par {d.agent_name}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="deal-detail-modal">
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="text-xs font-bold px-3 py-1 rounded-full bg-brand-lime/30 text-brand-greenDeep">{detail.property_type}</span>
                <h3 className="font-display text-2xl font-semibold text-brand-greenDeep mt-2">{detail.title}</h3>
                {detail.city && <p className="text-sm text-brand-green/55 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" />{detail.city}</p>}
              </div>
              <button onClick={() => setDetail(null)} className="p-2 rounded-full hover:bg-brand-softer"><X className="w-5 h-5 text-brand-green/60" /></button>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-5">
              <div className="bg-brand-softer rounded-xl px-3 py-3 text-center"><Euro className="w-4 h-4 mx-auto text-brand-green/50" /><p className="text-xs text-brand-green/45 mt-1">Prix d'achat</p><p className="font-semibold text-brand-greenDeep">{fmtEur(detail.purchase_price)}</p></div>
              <div className="bg-brand-softer rounded-xl px-3 py-3 text-center"><Building2 className="w-4 h-4 mx-auto text-brand-green/50" /><p className="text-xs text-brand-green/45 mt-1">Travaux</p><p className="font-semibold text-brand-greenDeep">{fmtEur(detail.works_estimate)}</p></div>
              <div className="bg-brand-softer rounded-xl px-3 py-3 text-center"><Percent className="w-4 h-4 mx-auto text-brand-green/50" /><p className="text-xs text-brand-green/45 mt-1">Rentabilité</p><p className="font-semibold text-emerald-600">{detail.rentability || 0}%</p></div>
            </div>
            {detail.photos?.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-5" data-testid="deal-detail-gallery">
                {detail.photos.map((p, i) => (
                  <a key={i} href={fileUrl(p)} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden border border-brand-soft aspect-[4/3]">
                    <img src={fileUrl(p)} alt={`photo ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                  </a>
                ))}
              </div>
            )}
            {[["Détail du projet", detail.project_detail], ["Raisons d'investir dans ce bien", detail.reasons], ["Les atouts du secteur", detail.sector_strengths], ["Hypothèses d'exploitation & scénarios de loyers", detail.rent_hypotheses]].map(([label, val]) => val ? (
              <div key={label} className="mt-5">
                <h4 className="font-semibold text-brand-greenDeep text-sm">{label}</h4>
                <p className="text-sm text-brand-green/70 mt-1 leading-relaxed whitespace-pre-line">{val}</p>
              </div>
            ) : null)}
            {hasSimData(detail) && (
              <div className="mt-5" data-testid="deal-detail-sim">
                <h4 className="font-semibold text-brand-greenDeep text-sm flex items-center gap-2"><Calculator className="w-4 h-4" /> Données d'investissement & d'exploitation</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                  {SIM_FIELDS.filter(({ k }) => Number(detail.sim?.[k]) > 0).map(({ k, label }) => (
                    <div key={k} className="bg-brand-softer rounded-xl px-3 py-2" data-testid={`deal-detail-sim-${k}`}>
                      <p className="text-xs text-brand-green/45 leading-tight">{label}</p>
                      <p className="font-semibold text-brand-greenDeep">{Number(detail.sim[k]).toLocaleString("fr-FR")}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {detail.documents?.length > 0 && (
              <div className="mt-5" data-testid="deal-detail-documents">
                <h4 className="font-semibold text-brand-greenDeep text-sm mb-2">Documents</h4>
                <div className="space-y-2">
                  {detail.documents.map((doc, i) => (
                    <a key={i} href={fileUrl(doc.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-brand-soft hover:bg-brand-softer text-sm text-brand-greenDeep transition-colors">
                      <FileText className="w-4 h-4 text-brand-green/60" /> <span className="truncate flex-1">{doc.filename || `Document ${i + 1}`}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs text-brand-green/40 mt-6">Publié par {detail.agent_name}</p>
            {hasSimData(detail) && (
              <button onClick={() => analyze(detail)} className="mt-4 w-full py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold inline-flex items-center justify-center gap-2 transition-colors" data-testid="analyze-deal-detail-btn">
                <Calculator className="w-4 h-4" /> Analyser la rentabilité de cet investissement
              </button>
            )}
            {hasSimData(detail) && (
              <button onClick={() => saveSimScenario(detail)} disabled={savingSim} className="mt-3 w-full py-3 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60" data-testid="save-sim-deal-btn">
                {savingSim ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />} Enregistrer la simulation dans « Mes scénarios »
              </button>
            )}
            {detail.agent_id !== user?.user_id && (
              <button onClick={() => contactAgent(detail)} className="mt-3 w-full py-3 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors" data-testid="contact-agent-btn">
                <MessagesSquare className="w-4 h-4" /> Contacter l'agent
              </button>
            )}
          </div>
        </div>
      )}

      {/* Create / edit modal */}
      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setForm(null)}>
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="deal-form-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-2xl font-semibold text-brand-greenDeep">{form.id ? "Modifier l'annonce" : "Publier une annonce"}</h3>
              <button onClick={() => setForm(null)} className="p-2 rounded-full hover:bg-brand-softer"><X className="w-5 h-5 text-brand-green/60" /></button>
            </div>
            <div className="space-y-4 mt-5">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Titre de l'annonce *"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inp} data-testid="deal-title" placeholder="Ex: T3 rentable à Lyon 7e" /></Field>
                <Field label="Ville"><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inp} placeholder="Lyon" /></Field>
              </div>
              <Field label="Type de bien">
                <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className={inp}>
                  {PROPERTY_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Prix d'achat (€)"><input type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className={inp} /></Field>
                <Field label="Travaux estimés (€)"><input type="number" value={form.works_estimate} onChange={(e) => setForm({ ...form, works_estimate: e.target.value })} className={inp} /></Field>
                <Field label="Rentabilité (%)"><input type="number" step="0.1" value={form.rentability} onChange={(e) => setForm({ ...form, rentability: e.target.value })} className={inp} /></Field>
              </div>

              <div className="rounded-2xl border border-brand-soft bg-brand-softer/40 p-4" data-testid="deal-sim-section">
                <p className="text-sm font-semibold text-brand-greenDeep flex items-center gap-2"><Calculator className="w-4 h-4" /> Données d'investissement & d'exploitation</p>
                <p className="text-xs text-brand-green/50 mt-0.5 mb-3">Renseignez ces éléments pour que l'investisseur puisse analyser la rentabilité en un clic dans le simulateur.</p>
                <div className="grid md:grid-cols-3 gap-3">
                  {SIM_FIELDS.map(({ k, label }) => (
                    <Field key={k} label={label}>
                      <input type="number" step="0.01" value={form.sim?.[k] ?? ""} onChange={(e) => setForm({ ...form, sim: { ...(form.sim || {}), [k]: e.target.value } })} className={inp} data-testid={`deal-sim-${k}`} />
                    </Field>
                  ))}
                </div>
              </div>
              <Field label="Détail du projet"><textarea rows={3} value={form.project_detail} onChange={(e) => setForm({ ...form, project_detail: e.target.value })} className={inp} /></Field>
              <Field label="Raisons d'investir dans ce bien"><textarea rows={2} value={form.reasons} onChange={(e) => setForm({ ...form, reasons: e.target.value })} className={inp} /></Field>
              <Field label="Les atouts du secteur"><textarea rows={2} value={form.sector_strengths} onChange={(e) => setForm({ ...form, sector_strengths: e.target.value })} className={inp} /></Field>
              <Field label="Hypothèses d'exploitation & scénarios de loyers"><textarea rows={2} value={form.rent_hypotheses} onChange={(e) => setForm({ ...form, rent_hypotheses: e.target.value })} className={inp} /></Field>

              <div>
                <span className="text-sm font-medium text-brand-green/70 mb-1.5 block">Photos du bien</span>
                <div className="flex flex-wrap gap-3">
                  {(form.photos || []).map((p, i) => (
                    <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-brand-soft group">
                      <img src={fileUrl(p)} alt={`photo ${i + 1}`} className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 p-1 rounded-full bg-black/55 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid="remove-photo-btn"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => photoInput.current?.click()} disabled={uploading === "photo"} className="w-24 h-24 rounded-xl border-2 border-dashed border-brand-soft hover:border-brand-lime flex flex-col items-center justify-center gap-1 text-brand-green/50 text-xs disabled:opacity-60" data-testid="add-photo-btn">
                    {uploading === "photo" ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ImagePlus className="w-5 h-5" /> Ajouter</>}
                  </button>
                </div>
                <input ref={photoInput} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhotos(Array.from(e.target.files || []))} data-testid="photo-input" />
              </div>

              <div>
                <span className="text-sm font-medium text-brand-green/70 mb-1.5 block">Documents (PDF, Word, Excel, images)</span>
                <div className="space-y-2">
                  {(form.documents || []).map((doc, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-xl border border-brand-soft text-sm">
                      <FileText className="w-4 h-4 text-brand-green/60" /> <span className="truncate flex-1 text-brand-greenDeep">{doc.filename || `Document ${i + 1}`}</span>
                      <button type="button" onClick={() => removeDoc(i)} className="p-1 rounded-full hover:bg-red-50 text-red-500" data-testid="remove-doc-btn"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => docInput.current?.click()} disabled={uploading === "doc"} className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold disabled:opacity-60" data-testid="add-doc-btn">
                  {uploading === "doc" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} Joindre un document
                </button>
                <input ref={docInput} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,image/*" multiple className="hidden" onChange={(e) => uploadDocs(Array.from(e.target.files || []))} data-testid="doc-input" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setForm(null)} className="flex-1 py-3 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold">Annuler</button>
              <button onClick={save} disabled={busy} className="flex-1 py-3 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="save-deal-btn">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {form.id ? "Enregistrer" : "Publier"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full px-4 py-2.5 rounded-xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm text-brand-greenDeep";
const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-brand-green/70 mb-1.5 block">{label}</span>
    {children}
  </label>
);
